import React, { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Button, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import dayjs from "dayjs";
import { useActionHook } from "hooks";
import { useDocumentsActions } from "store";

interface Props {
  onCancel: () => void;
  onSubmit: (value: number) => void;
}

export const CreateSectionFromMeetingContent: React.FC<Props> = ({ onCancel, onSubmit }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const { getMeetingsAction } = useDocumentsActions();

  const { action: getMeetings, data: meetings, loading: meetingsLoading } = useActionHook(getMeetingsAction);

  useEffect(() => {
    getMeetings();
  }, []);

  const filteredMeetings = meetings?.filter((meeting) => {
    const matchesSearch =
      meeting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      meeting.participants.some((participant) => participant.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesDate = selectedDate
      ? dayjs(meeting.meeting_date).format("DD/MM/YYYY") === dayjs(selectedDate).format("DD/MM/YYYY")
      : true;

    return matchesSearch && matchesDate;
  });

  return (
    <ProgressOverlay
      containerSX={{
        ...style,
        display: "flex",
        flexDirection: "column",
        width: 425,
        p: "32px",
        borderRadius: "8px",
        bgcolor: "white",
        gap: "24px",
        boxShadow:
          "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
      }}
      inProgress={meetingsLoading}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ borderBottom: "1px solid #DAE0E2" }}
      >
        <Typography>Add Meeting</Typography>
        <IconButton onClick={onCancel}>
          <CloseIcon className="icon" />
        </IconButton>
      </Stack>
      <Stack sx={{ borderBottom: "1px solid #DAE0E2", pb: 2 }} gap={2}>
        <Stack direction="row" spacing={2}>
          <DesktopDatePicker
            format="DD/MM/YYYY"
            value={selectedDate ? dayjs(selectedDate) : null}
            onChange={(date) => setSelectedDate(date ? date.toISOString() : null)}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                placeholder: "Select date...",
                InputProps: {
                  sx: {
                    backgroundColor: "#F7F9FA",
                    "& fieldset": {
                      border: "none"
                    },
                    "&:hover fieldset": {
                      border: "none"
                    },
                    "&.Mui-focused fieldset": {
                      border: "none"
                    }
                  }
                }
              },
              field: {
                clearable: true,
                onClear: () => setSelectedDate(null)
              }
            }}
          />
          <TextField
            fullWidth
            size="small"
            placeholder="Search meetings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: "#5D7782" }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: "#F7F9FA",
                "& fieldset": {
                  border: "none"
                },
                "&:hover fieldset": {
                  border: "none"
                },
                "&.Mui-focused fieldset": {
                  border: "none"
                }
              }
            }}
          />
        </Stack>
      </Stack>
      <Stack
        sx={{
          maxHeight: "300px",
          minHeight: "300px",
          overflow: "auto"
        }}
        gap="10px"
      >
        {filteredMeetings?.length ? (
          filteredMeetings.map((meeting, index) => (
            <Stack
              onClick={() => onSubmit(meeting.id)}
              sx={{
                p: "5px",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px"
                }
              }}
              key={index}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#0A3142" }}>{meeting.title}</Typography>
              <Typography sx={{ fontSize: "13px", fontWeight: "400", color: "#5D7782" }}>
                {dayjs(meeting.meeting_date).format("DD/MM/YYYY")} {meeting.meeting_time_start} -{" "}
                {meeting.meeting_time_end}
              </Typography>
              <Stack direction="row" gap="5px" alignItems="center">
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: "400",
                    color: "#0A3142",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%"
                  }}
                >
                  {meeting.participants.join(", ")}
                </Typography>
              </Stack>
            </Stack>
          ))
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              height: "100%",
              color: "#5D7782",
              textAlign: "center",
              p: 2
            }}
          >
            <Typography sx={{ fontSize: "14px", mb: 1 }}>No meetings found</Typography>
            {Boolean(meetings?.length) && (
              <Typography sx={{ fontSize: "13px", color: "#8795A1" }}>Try adjusting your search term</Typography>
            )}
          </Stack>
        )}
      </Stack>

      <Stack direction="row" gap="10px" justifyContent="flex-end">
        <Button variant="outlined" sx={{ minWidth: "90px" }} onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </ProgressOverlay>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "10px"
};

// how to use
// {async () => {
//   const data = await openPrompt({ Component: ExistingMeetingNoteContent });
//   console.log(data);
// }}
